import { STORAGE_KEYS } from "../storage-keys";
import {
	hasIncompleteInventory,
	INCOMPLETE_INVENTORY_KEY,
} from "./backup-inventory";
import {
	type CloudBackupRefreshResult,
	flushCloudBackupIfStale,
	markCloudBackupStale,
} from "./cloud-refresh";

type BackupStorage = Pick<Storage, "getItem" | "setItem" | "removeItem">;
export type PortableCommitResult =
	| { success: true; portablePersisted: true }
	| {
			success: false;
			portablePersisted: false;
			error: string;
			rollbackFailed: boolean;
	  };

function restore(
	storage: BackupStorage,
	key: string,
	value: string | null
): void {
	if (value === null) {
		storage.removeItem(key);
	} else {
		storage.setItem(key, value);
	}
}
/** No awaits between storage writes. This is best-effort rollback, not a browser transaction. */
export function commitPortableBackup(
	serialized: string,
	encrypted: string,
	local: BackupStorage,
	session: BackupStorage
): PortableCommitResult {
	let previousLocal: string | null;
	let previousSession: string | null;
	let previousInventory: string | null;
	try {
		previousInventory = local.getItem(INCOMPLETE_INVENTORY_KEY);
		previousLocal = local.getItem(STORAGE_KEYS.ENCRYPTED_BACKUP);
		previousSession = session.getItem(STORAGE_KEYS.DECRYPTED_BACKUP);
	} catch {
		return {
			success: false,
			portablePersisted: false,
			rollbackFailed: false,
			error: "Backup storage is unavailable",
		};
	}
	let localWritten = false;
	try {
		local.setItem(STORAGE_KEYS.ENCRYPTED_BACKUP, encrypted);
		localWritten = true;
		session.setItem(STORAGE_KEYS.DECRYPTED_BACKUP, serialized);
		if (hasIncompleteInventory(JSON.parse(serialized))) {
			local.setItem(INCOMPLETE_INVENTORY_KEY, encrypted);
		} else if (previousInventory !== null) {
			local.removeItem(INCOMPLETE_INVENTORY_KEY);
		}
		return { success: true, portablePersisted: true };
	} catch {
		let rollbackFailed = false;
		// A failed local write cannot have changed the session.
		if (localWritten) {
			for (const [storage, key, value] of [
				[local, STORAGE_KEYS.ENCRYPTED_BACKUP, previousLocal],
				[local, INCOMPLETE_INVENTORY_KEY, previousInventory],
				[session, STORAGE_KEYS.DECRYPTED_BACKUP, previousSession],
			] as const) {
				try {
					restore(storage, key, value);
				} catch {
					rollbackFailed = true;
				}
			}
		}
		return {
			success: false,
			portablePersisted: false,
			rollbackFailed,
			error: rollbackFailed
				? "Backup write failed and previous storage could not be fully restored"
				: "Failed to persist backup",
		};
	}
}

/** A slow PRF cleanup must not enqueue cloud work for a different current backup. */
export async function refreshCommittedCloudBackup(
	encrypted: string,
	defer?: boolean
): Promise<CloudBackupRefreshResult | { status: "deferred" }> {
	try {
		if (localStorage.getItem(STORAGE_KEYS.ENCRYPTED_BACKUP) !== encrypted) {
			return { status: "skipped", reason: "superseded" };
		}
		markCloudBackupStale();
		return defer ? { status: "deferred" } : await flushCloudBackupIfStale();
	} catch {
		return { status: "failed", error: "Cloud backup refresh failed" };
	}
}
